import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import ActionDialog from "components/admin/ActionDialog";
import GameIcon from "components/admin/GameIcon";
import IconPicker from "components/admin/IconPicker";
import TemplatePicker from "components/admin/TemplatePicker";
import { useRunner } from "components/admin/hooks";
import api from "api/client";

/** Dải mã vật phẩm event nhân bản (ItemClone.RANGE_FROM–RANGE_TO trên GServer). */
export const EVENT_ITEM_FROM = 249000;
export const EVENT_ITEM_TO = 249999;

export const isEventItem = (id) => Number(id) >= EVENT_ITEM_FROM && Number(id) <= EVENT_ITEM_TO;

const itemCache = new Map();

function loadItem(itemId, fresh) {
  const id = Number(itemId);
  if (!id) return Promise.resolve(null);
  if (!fresh && itemCache.has(id)) return itemCache.get(id);
  const promise = api
    .get("/api/admin/templates/items", { q: String(id), size: 5 })
    .then((res) => res.items.find((x) => x.itemId === id) || null)
    .catch(() => null);
  itemCache.set(id, promise);
  return promise;
}

/** Đọc tên, mô tả, ảnh của một vật phẩm. version đổi thì đọc lại (sau khi sửa). */
export function useItem(itemId, version = 0) {
  const [item, setItem] = useState(null);
  useEffect(() => {
    let active = true;
    loadItem(itemId, version > 0).then((res) => active && setItem(res));
    return () => {
      active = false;
    };
  }, [itemId, version]);
  return item;
}

/** Tên vật phẩm kèm icon nhỏ, dùng trong ô chọn và bảng. */
export function ItemName({ itemId }) {
  const item = useItem(itemId);
  return (
    <MDBox component="span" display="inline-flex" alignItems="center" gap={0.5}>
      {item?.iconPath && <GameIcon path={item.iconPath} size={20} />}
      {item?.name || (itemId ? `#${itemId}` : "Chưa gán")}
    </MDBox>
  );
}

ItemName.defaultProps = { itemId: 0 };
ItemName.propTypes = { itemId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) };

/** Vai trò của vật phẩm trong mùa, suy ra từ cấu hình, để người vận hành biết món này dùng làm gì. */
export function roleUses(config, role) {
  const uses = [];
  const craft = config?.modules?.farm_craft;
  (craft?.recipes || []).forEach((recipe) => {
    if (recipe.out === role) uses.push("Thành phẩm khi chế");
    if (recipe.need && Object.prototype.hasOwnProperty.call(recipe.need, role)) {
      uses.push("Nguyên liệu");
    }
  });
  const dropRoles = craft?.drop?.items;
  if (craft?.drop && (dropRoles ? dropRoles.includes(role) : uses.includes("Nguyên liệu"))) {
    uses.push("Rơi khi đánh quái");
  }
  const use = craft?.use?.[role];
  if (use) uses.push(use.gift ? "Mở ra quà" : "Dùng được điểm");
  if (config?.modules?.event_boss?.drop === role) uses.push("Boss rơi");
  if (config?.modules?.gift?.item === role) uses.push("Quà tặng bạn");
  return [...new Set(uses)];
}

/** Tạo bản riêng (clone) hoặc sửa tên / mô tả / ảnh của bản riêng. */
function ItemFormDialog({ mode, item, onClose, onDone }) {
  const [run, busy] = useRunner();
  const [form, setForm] = useState({
    name: item?.name || "",
    description: item?.description || "",
    iconPath: item?.iconPath || "",
  });
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const cloning = mode === "clone";

  const submit = async () => {
    const { ok, result } = await run(
      () =>
        cloning
          ? api.post(`/api/admin/event/items/${item.itemId}/clone`, form)
          : api.put(`/api/admin/event/items/${item.itemId}`, form),
      cloning ? (res) => `Đã tạo vật phẩm #${res.itemId}` : "Đã lưu vật phẩm"
    );
    if (ok) onDone(result.itemId);
  };

  return (
    <ActionDialog
      open
      maxWidth="sm"
      title={cloning ? `Tạo bản riêng từ "${item?.name}"` : `Sửa vật phẩm #${item?.itemId}`}
      description={
        cloning
          ? "Tạo một vật phẩm mới giống hệt món gốc (công dụng, cộng dồn…) nhưng tên và ảnh của riêng mùa này. Món gốc và đồ người chơi đang giữ không đổi."
          : "Đổi ngay cho mọi người chơi. Người đang online thấy ảnh mới sau khi đăng nhập lại, tên đổi ngay."
      }
      color="info"
      submitText={cloning ? "Tạo bản riêng" : "Lưu"}
      busy={busy}
      disabled={!form.name.trim() || !form.iconPath}
      onClose={onClose}
      onSubmit={submit}
    >
      <MDInput
        fullWidth
        label="Tên vật phẩm"
        value={form.name}
        inputProps={{ maxLength: 64 }}
        onChange={(e) => set({ name: e.target.value })}
      />
      <MDInput
        fullWidth
        multiline
        minRows={2}
        label="Mô tả"
        value={form.description}
        inputProps={{ maxLength: 512 }}
        onChange={(e) => set({ description: e.target.value })}
      />
      <IconPicker
        folder="items"
        label="Ảnh vật phẩm"
        value={form.iconPath}
        onChange={(iconPath) => set({ iconPath })}
      />
    </ActionDialog>
  );
}

ItemFormDialog.propTypes = {
  mode: PropTypes.oneOf(["clone", "edit"]).isRequired,
  item: PropTypes.shape({
    itemId: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    iconPath: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function PickItemDialog({ value, onClose, onPick }) {
  const [itemId, setItemId] = useState(value);
  return (
    <ActionDialog
      open
      maxWidth="sm"
      title="Chọn vật phẩm khác"
      description="Gõ tên hoặc mã để tìm. Nên chọn vật phẩm event (mã 249000 trở lên) hoặc tạo bản riêng."
      color="info"
      submitText="Dùng vật phẩm này"
      disabled={!itemId}
      onClose={onClose}
      onSubmit={() => onPick(Number(itemId))}
    >
      <TemplatePicker
        kind="item"
        label="Vật phẩm"
        value={itemId}
        onChange={setItemId}
        fullWidth
        hideAdminItems
      />
    </ActionDialog>
  );
}

PickItemDialog.propTypes = {
  value: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
};

function ItemRow({ role, itemId, uses, locked, onChange }) {
  const [version, setVersion] = useState(0);
  const item = useItem(itemId, version);
  const [dialog, setDialog] = useState(null);
  const own = isEventItem(itemId);

  return (
    <MDBox
      display="flex"
      alignItems="center"
      gap={2}
      p={1.5}
      borderRadius="lg"
      flexWrap="wrap"
      sx={{ border: "1px solid", borderColor: "divider" }}
    >
      <MDBox
        width={56}
        height={56}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="md"
        sx={{ bgcolor: "action.hover" }}
      >
        <GameIcon path={item?.iconPath} size={40} />
      </MDBox>
      <MDBox flex={1} minWidth={220}>
        <MDTypography variant="button" fontWeight="medium" display="block">
          {item?.name || `#${itemId}`}{" "}
          <MDTypography component="span" variant="caption" color="text">
            #{itemId} · vai trò &quot;{role}&quot;
          </MDTypography>
        </MDTypography>
        <MDBox display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
          <Chip
            size="small"
            color={own ? "success" : "warning"}
            label={own ? "Bản riêng của event" : "Vật phẩm gốc, dùng chung"}
          />
          {uses.map((use) => (
            <Chip key={use} size="small" variant="outlined" label={use} />
          ))}
        </MDBox>
      </MDBox>
      <MDBox display="flex" gap={1} flexWrap="wrap">
        {own ? (
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            disabled={!item}
            onClick={() => setDialog("edit")}
          >
            <Icon>edit</Icon>&nbsp;Sửa tên / ảnh
          </MDButton>
        ) : (
          !locked && (
            <MDButton
              size="small"
              variant="gradient"
              color="info"
              disabled={!item}
              onClick={() => setDialog("clone")}
            >
              <Icon>content_copy</Icon>&nbsp;Tạo bản riêng (đổi tên, ảnh)
            </MDButton>
          )
        )}
        {!locked && (
          <MDButton size="small" variant="text" color="secondary" onClick={() => setDialog("pick")}>
            Chọn món khác
          </MDButton>
        )}
      </MDBox>
      {(dialog === "clone" || dialog === "edit") && item && (
        <ItemFormDialog
          mode={dialog}
          item={item}
          onClose={() => setDialog(null)}
          onDone={(newId) => {
            setDialog(null);
            if (dialog === "clone") onChange(newId);
            else setVersion((v) => v + 1);
          }}
        />
      )}
      {dialog === "pick" && (
        <PickItemDialog
          value={Number(itemId)}
          onClose={() => setDialog(null)}
          onPick={(newId) => {
            setDialog(null);
            onChange(newId);
          }}
        />
      )}
    </MDBox>
  );
}

ItemRow.propTypes = {
  role: PropTypes.string.isRequired,
  itemId: PropTypes.number.isRequired,
  uses: PropTypes.arrayOf(PropTypes.string).isRequired,
  locked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
};

/** Các vật phẩm theo vai trò của mùa (config.items). */
function ItemsEditor({ config, locked, update }) {
  const items = config.items || {};
  const roles = Object.keys(items).filter((role) => Number(items[role]) > 0);
  const shared = roles.filter((role) => !isEventItem(items[role]));

  if (roles.length === 0) {
    return <Alert severity="info">Loại event này không dùng vật phẩm.</Alert>;
  }
  return (
    <MDBox display="flex" flexDirection="column" gap={1.5}>
      {locked ? (
        <Alert severity="info">
          Mùa đã bắt đầu nên không đổi được vật phẩm. Vẫn sửa được tên và ảnh của bản riêng.
        </Alert>
      ) : (
        shared.length > 0 && (
          <Alert severity="warning">
            {shared.length} vật phẩm đang là món gốc dùng chung với mùa khác. Muốn đổi tên hoặc ảnh
            theo chủ đề mới, bấm <b>Tạo bản riêng</b> ở từng món. Món gốc không bị đổi.
          </Alert>
        )
      )}
      {roles.map((role) => (
        <ItemRow
          key={role}
          role={role}
          itemId={Number(items[role])}
          uses={roleUses(config, role)}
          locked={locked}
          onChange={(newId) =>
            update((next) => {
              next.items = { ...(next.items || {}), [role]: newId };
            })
          }
        />
      ))}
    </MDBox>
  );
}

ItemsEditor.propTypes = {
  config: PropTypes.shape({ items: PropTypes.shape({}) }).isRequired,
  locked: PropTypes.bool.isRequired,
  update: PropTypes.func.isRequired,
};

export default ItemsEditor;
